import React, { createContext, useState, useEffect, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';

// Create context
export const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Check if token exists and is valid on initial load
  useEffect(() => {
    const verifyToken = () => {
      const storedToken = localStorage.getItem('token');
      
      if (!storedToken) {
        setIsAuthenticated(false);
        setCurrentUser(null);
        setIsLoading(false);
        return;
      }
      
      try {
        // Decode token to get expiration
        const decoded = jwtDecode(storedToken);
        
        // Check if token is expired
        if (decoded.exp * 1000 < Date.now()) {
          // Token expired, remove from storage
          localStorage.removeItem('token');
          setIsAuthenticated(false);
          setCurrentUser(null);
          setToken(null);
        } else {
          // Token valid, set user and authentication state
          setCurrentUser(decoded);
          setIsAuthenticated(true);
          setToken(storedToken);
        }
      } catch (error) {
        console.error('Invalid token:', error);
        localStorage.removeItem('token');
        setIsAuthenticated(false);
        setCurrentUser(null);
        setToken(null);
      }
      
      setIsLoading(false);
    };
    
    verifyToken();
  }, []);

  // Login function
  const login = (userData, token) => {
    localStorage.setItem('token', token);
    setToken(token);
    setCurrentUser(userData);
    setIsAuthenticated(true);
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setCurrentUser(null);
    setIsAuthenticated(false);
  };

  // Update user function
  const updateUser = (userData) => {
    setCurrentUser(userData);
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isAuthenticated,
        isLoading,
        token,
        login,
        logout,
        updateUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};